import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Check, AlertCircle, ChevronRight } from 'lucide-react';
import { getMacroStatus, getToleranceRanges } from '../utils/macroStatus';
import { getTodayString } from '../utils/dateUtils';
import { formatMacro } from '../utils/nutritionFormat';
import { hasRealNutritionTargets } from '../utils/nutritionTargets';
import NutritionTargetsSetupPrompt from './NutritionTargetsSetupPrompt';

/**
 * TodaySummaryCard — Home progress summary.
 *
 * Compact layout: calories hero + 3-up macro mini-grid.
 * Range logic still drives status internally (low / on / over) but
 * MIN / GOAL / MAX values are intentionally NOT displayed here. The
 * detailed range visualisation lives in the Plan/Daily view.
 */

function statusFor(loggedVal, goalVal, range) {
  // range = [lo%, hi%] of goal — same on_track logic the rest of the app uses.
  var rangeMin = Math.round(goalVal * range[0] / 100);
  var rangeMax = Math.round(goalVal * range[1] / 100);
  var v = Math.round(loggedVal);
  if (v < rangeMin) return 'low';
  if (v > rangeMax) return 'over';
  return 'on';
}

function colorFor(status) {
  if (status === 'on')   return 'hsl(189, 90%, 53%)';   // accent / cyan
  if (status === 'over') return '#f59e0b';              // amber
  return 'hsl(189, 90%, 78%)';                          // muted cyan (low)
}

function statusLabel(status) {
  if (status === 'on')   return 'On track';
  if (status === 'over') return 'Over';
  return 'Low';
}

function statusTextClass(status) {
  if (status === 'on')   return 'text-accent';
  if (status === 'over') return 'text-amber-600';
  return 'text-muted-foreground';
}

/**
 * Roll up the four macro statuses into a single overall summary word.
 * Priority: any 'over' wins, then all 'on' = on, otherwise low.
 */
function overallStatus(statuses) {
  for (var i = 0; i < statuses.length; i++) {
    if (statuses[i] === 'over') return 'over';
  }
  for (var j = 0; j < statuses.length; j++) {
    if (statuses[j] !== 'on') return 'low';
  }
  return 'on';
}

function MacroMini(props) {
  var label = props.label;
  var unit = props.unit || 'g';
  var loggedVal = Math.round(props.loggedVal || 0);
  var loggedDisplay = formatMacro(props.loggedVal || 0);
  var goalVal = Math.round(props.goalVal || 0);
  var status = props.status;
  var color = colorFor(status);
  var pct = goalVal > 0 ? Math.min((loggedVal / goalVal) * 100, 100) : 0;

  var remaining = goalVal - loggedVal;
  var rightLabel;
  if (status === 'over') rightLabel = '+' + Math.abs(remaining) + unit;
  else if (remaining > 0) rightLabel = remaining + unit + ' left';
  else rightLabel = 'Complete';

  return (
    <div data-testid={'macro-mini-' + label.toLowerCase()}>
      <div className="text-[11px] font-medium text-muted-foreground/80">{label}</div>
      <div className="flex items-baseline gap-1 mt-0.5">
        <span className="text-[15px] font-semibold tabular-nums">{loggedDisplay}{unit}</span>
        <span className="text-[10px] text-muted-foreground/70 tabular-nums">/ {goalVal}{unit}</span>
      </div>
      <div className="relative h-[4px] mt-1.5 rounded-full" style={{ backgroundColor: 'hsl(var(--muted-foreground) / 0.18)' }}>
        {pct > 0.5 && (
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-all duration-700 ease-out"
            style={{ width: pct + '%', backgroundColor: color }}
          />
        )}
      </div>
      <div className={'text-[10.5px] font-medium mt-1 tabular-nums ' + statusTextClass(status)}>
        {rightLabel}
      </div>
    </div>
  );
}

function TodaySummaryCard({ profile, todayPlan, mealDetails }) {
  var navigate = useNavigate();

  // Never render a hardcoded 2000/150/200/70 profile as if it were the
  // user's own personalized targets — route them to finish setup instead.
  if (!hasRealNutritionTargets(profile)) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 0 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-2"
        data-testid="today-summary-card"
      >
        <h3 className="font-heading font-semibold text-base px-1">Today&apos;s Progress</h3>
        <NutritionTargetsSetupPrompt />
      </motion.div>
    );
  }

  var goal = {
    calories: profile?.target_calories || profile?.calories || 2000,
    protein: profile?.target_protein_g || profile?.protein || 150,
    carbs: profile?.target_carbs_g || profile?.carbs || 200,
    fat: profile?.target_fat_g || profile?.fat || 70
  };
  var userGoal = profile?.goal || 'Maintain';
  var ranges = getToleranceRanges(userGoal);
  var onTrack = ranges.on_track;

  // Sum logged + planned macros from today's plan
  var logged = { calories: 0, protein: 0, carbs: 0, fat: 0 };
  var totalPlanned = { calories: 0, protein: 0, carbs: 0, fat: 0 };
  var hasPlannedMeals = todayPlan.length > 0;
  var plannedMealCount = 0;
  var loggedMealCount = 0;

  for (var i = 0; i < todayPlan.length; i++) {
    var plan = todayPlan[i];
    // Skipped meals stay visible in the Skipped section but are no longer
    // part of today's active plan (matches the backend nutrition summary and
    // the Plan page). Logging one flips it to `completed`, so it counts again.
    if (plan.status === 'skipped') continue;
    var planMacros = plan.macros;
    var meal = mealDetails[plan.meal_id];
    var macros = {
      calories: planMacros?.calories ?? meal?.calories ?? 0,
      protein: planMacros?.protein ?? meal?.protein ?? 0,
      carbs: planMacros?.carbs ?? meal?.carbs ?? 0,
      fat: planMacros?.fat ?? meal?.fat ?? 0
    };
    if (macros.calories > 0 || macros.protein > 0) {
      totalPlanned.calories += macros.calories;
      totalPlanned.protein += macros.protein;
      totalPlanned.carbs += macros.carbs;
      totalPlanned.fat += macros.fat;
      plannedMealCount++;
      if (plan.status === 'completed') {
        logged.calories += macros.calories;
        logged.protein += macros.protein;
        logged.carbs += macros.carbs;
        logged.fat += macros.fat;
        loggedMealCount++;
      }
    }
  }

  // Per-macro range status (drives bar color + per-card delta)
  var calStatus = statusFor(logged.calories, goal.calories, onTrack.calories);
  var proStatus = statusFor(logged.protein,  goal.protein,  onTrack.protein);
  var carbStatus = statusFor(logged.carbs,    goal.carbs,    onTrack.carbs);
  var fatStatus = statusFor(logged.fat,      goal.fat,      onTrack.fat);

  // Overall summary word reflects ALL four macros, not just calories.
  var overall = overallStatus([calStatus, proStatus, carbStatus, fatStatus]);

  // Calories hero metrics
  var calColor = colorFor(calStatus);
  var calPct = goal.calories > 0 ? Math.min((logged.calories / goal.calories) * 100, 100) : 0;
  var calRemaining = Math.round(goal.calories - logged.calories);
  var calRightLabel;
  if (calStatus === 'over') calRightLabel = '+' + Math.abs(calRemaining) + ' over';
  else if (calRemaining > 0) calRightLabel = calRemaining + ' left';
  else calRightLabel = 'Complete';

  // Plan-status chip logic (unchanged from previous version):
  //   1) Completion  — logged totals meet ALL four target ranges
  //   2) Plan valid  — plan is on-track and day not yet completed
  //   3) Adjustment  — plan no longer valid (or no meals planned)
  var planStatus = hasPlannedMeals ? getMacroStatus(totalPlanned, goal, userGoal) : null;
  var planIsOnTrack = planStatus === 'on-track';
  var loggedStatusFull = loggedMealCount > 0 ? getMacroStatus(logged, goal, userGoal) : null;
  var loggedHitsTargets = loggedStatusFull === 'on-track';
  var isCompletion = hasPlannedMeals && loggedHitsTargets;

  var statusVariant;
  var statusMessage;
  if (isCompletion) {
    statusVariant = 'completion';
    statusMessage = 'You hit your nutrition targets today';
  } else if (hasPlannedMeals && planIsOnTrack) {
    statusVariant = 'plan-valid';
    statusMessage = "Today's plan hits your nutrition targets";
  } else if (hasPlannedMeals) {
    statusVariant = 'adjustment';
    statusMessage = 'Your plan needs adjustment to hit today\u2019s targets';
  } else {
    statusVariant = 'empty';
    statusMessage = 'No meals planned';
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 0 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border/60 rounded-2xl p-5 shadow-sm space-y-4"
      data-testid="today-summary-card"
    >
      {/* Header — title + overall status word (rolled up across all 4 macros) */}
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold text-base">Today&apos;s Progress</h3>
        <span
          className={'text-[11px] font-medium ' + statusTextClass(overall)}
          data-testid="overall-status"
        >
          {statusLabel(overall)}
        </span>
      </div>

      {/* Calories hero */}
      <div data-testid="calories-hero">
        <div className="flex items-baseline justify-between">
          <span className="text-[11px] font-medium text-muted-foreground/80">Calories</span>
          <span
            className={'text-[11px] font-medium tabular-nums ' + statusTextClass(calStatus)}
            data-testid="calories-remaining"
          >
            {calRightLabel}
          </span>
        </div>
        <div className="flex items-baseline gap-1.5 mt-0.5">
          <span className="text-[22px] font-bold tabular-nums leading-none">
            {Math.round(logged.calories)}
          </span>
          <span className="text-[12px] text-muted-foreground/70 tabular-nums">
            / {Math.round(goal.calories)} cal
          </span>
        </div>
        <div className="relative h-[5px] mt-2 rounded-full" style={{ backgroundColor: 'hsl(var(--muted-foreground) / 0.18)' }}>
          {calPct > 0.5 && (
            <div
              className="absolute inset-y-0 left-0 rounded-full transition-all duration-700 ease-out"
              style={{ width: calPct + '%', backgroundColor: calColor }}
            />
          )}
        </div>
      </div>

      {/* 3-up macro mini-grid */}
      <div className="grid grid-cols-3 gap-4 pt-1">
        <MacroMini label="Protein" unit="g" loggedVal={logged.protein} goalVal={goal.protein} status={proStatus} />
        <MacroMini label="Carbs"   unit="g" loggedVal={logged.carbs}   goalVal={goal.carbs}   status={carbStatus} />
        <MacroMini label="Fat"     unit="g" loggedVal={logged.fat}     goalVal={goal.fat}     status={fatStatus} />
      </div>

      {/* Plan status chip — keeps the existing routing to PlanningHub Daily tab */}
      <button
        onClick={function() {
          var today = getTodayString();
          navigate('/plan?date=' + today);
        }}
        className="w-full flex items-center justify-between p-2.5 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors"
        data-testid="plan-status-btn"
      >
        <div className="flex items-center gap-2">
          {statusVariant === 'completion' || statusVariant === 'plan-valid' ? (
            <>
              <div className="w-5 h-5 rounded-full bg-accent/15 flex items-center justify-center">
                <Check size={12} className="text-accent" />
              </div>
              <span className="text-[13px] font-medium text-accent" data-testid={'home-status-' + statusVariant}>
                {statusMessage}
              </span>
            </>
          ) : (
            <>
              <div className="w-5 h-5 rounded-full bg-amber-500/15 flex items-center justify-center">
                <AlertCircle size={12} className="text-amber-500" />
              </div>
              <span className="text-[13px] font-medium text-amber-600" data-testid={'home-status-' + statusVariant}>
                {statusMessage}
              </span>
            </>
          )}
        </div>
        <ChevronRight size={14} className="text-muted-foreground" />
      </button>
    </motion.div>
  );
}

export default TodaySummaryCard;
